import React, { useCallback, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faCalendarDay,
	faChevronDown,
	faChevronUp,
	faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import { CCBadge, CCEmptyState } from "@pymblecitizencoders/cc-ui";
import { CalendarEvent } from "../../types/event";
import { EventChip, chipClass, chipsFor } from "../../utils/eventChips";
import { hexToRgbTriple } from "../../utils/eventColour";
import { DayFocus, groupByDay } from "../../utils/eventDays";
import { formatTimeRange } from "../../utils/eventDates";
import { useDayWindow } from "../../hooks/useDayWindow";
import { Tooltip } from "../common/Tooltip";
import { ChipDetail } from "./ChipDetail";
import { BottomEdge, SearchEdges, TopEdge } from "./browseEdges";
import "./eventManager.css";

interface EventListRowsProps {
	/** Already filtered by the rail — this component does no filtering. */
	events: CalendarEvent[];
	onOpen: (id: string) => void;
	/** The day to start at, and the request that asked for it. See DayFocus. */
	focus?: DayFocus | null;
	/** Fetch the month before the earliest one loaded. */
	onLoadEarlier?: () => void;
	/** Fetch the month after the latest one loaded. */
	onLoadLater?: () => void;
	loadingEarlier?: boolean;
	loadingLater?: boolean;
	/**
	 * Present when these events came from a server-side search rather than from
	 * the month pager. The two page over different things, so the edges say
	 * different things: a result set has no earlier months to fetch, only more
	 * of itself. See browseEdges.
	 */
	search?: SearchEdges;
	/**
	 * Present only while a tag is armed for assignment. It changes what a click
	 * on a row MEANS — choose rather than open — which is why it arrives as one
	 * object rather than as a loose flag: there is no state in which half of it
	 * applies. Absent is the normal reading state, and costs the row nothing.
	 */
	selection?: ListSelection;
}

/** The selection the page owns; this component only reports clicks against it. */
export interface ListSelection {
	/** Ids chosen right now. */
	selected: Set<string>;
	onToggle: (id: string) => void;
}

/* Re-exported so the page can keep importing it from the view it belongs to.
   The definition moved to utils/eventDays, where useDayWindow can reach it
   without importing a component. */
export type { DayFocus };

/**
 * The List view: events grouped under their day, windowed to what is on screen.
 *
 * The reading order the design asks for is time, then title, then where, then
 * what kind of thing it is — so that skimming the left edge answers "what is on
 * today" without reading a single title. The accent bar down each row carries
 * the event's SOURCE colour, the same one the calendar tints its labels with,
 * so an Edumate event looks like an Edumate event in either view.
 *
 * WHOLE DAYS are mounted, never half of one, so a heading reading "12 events" is
 * always followed by twelve. useDayWindow owns which days those are, and asks
 * for another month when the window reaches either end.
 *
 * Day sections collapse. The state is local and deliberately not persisted: it
 * is a way to get one busy Friday out of the way while reading the rest of the
 * week, not a preference.
 */
const EventListRowsInner: React.FC<EventListRowsProps> = ({
	events,
	onOpen,
	focus,
	onLoadEarlier,
	onLoadLater,
	loadingEarlier,
	loadingLater,
	search,
	selection,
}) => {
	const days = useMemo(() => groupByDay(events), [events]);
	const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
	const rootRef = useRef<HTMLDivElement>(null);

	const { window, topRef, bottomRef, atStart, atEnd, canScroll } = useDayWindow(
		{
			days,
			rootRef,
			focus,
			/* Undefined in search mode: reaching the end of the results must not
			   ask the month pager for another month, which would fetch a range
			   nobody asked for and mix it into a result set. */
			onNeedEarlier: search ? undefined : onLoadEarlier,
			onNeedLater: search ? undefined : onLoadLater,
			loadingEarlier,
			loadingLater,
		},
	);

	/**
	 * Chips, worked out once per event rather than once per render.
	 *
	 * `chipsFor` walks the event's groups and allocates as it goes, so on a busy
	 * month it was doing that thousands of times over whenever an unrelated piece
	 * of state moved.
	 */
	const chips = useMemo(() => {
		const map = new Map<string, EventChip[]>();
		for (const evt of events) map.set(evt._id ?? String(evt.id), chipsFor(evt));
		return map;
	}, [events]);

	const toggleDay = useCallback((key: string) => {
		setCollapsed((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	}, []);

	if (days.length === 0) {
		return (
			<CCEmptyState
				icon={<FontAwesomeIcon icon={faCalendarDay} />}
				message={
					search ? "Nothing matches this search." : "No events in this range."
				}
				description={
					search
						? "The whole calendar was searched, so there is no other month to try. Use a different word, or clear a filter in the rail."
						: "Try another month, or clear a filter in the rail."
				}
			/>
		);
	}

	return (
		<div
			className={`em-list${selection ? " em-list--picking" : ""}`}
			ref={rootRef}
		>
			{/* Both sentinels always render, so the observer always has something to
			    watch. What they SAY changes: while there are days in hand above or
			    below, scrolling here mounts more of them; at the edge of what has
			    been fetched it asks for the next month, or offers the button that
			    does when the panel is too short to be scrolled at all. In search
			    mode it reports the result set instead. See browseEdges. */}
			<div className="em-browse-edge" ref={topRef}>
				<TopEdge
					atEdge={atStart}
					search={search}
					browse={{ loadingEarlier, onLoadEarlier, scrollable: canScroll }}
				/>
			</div>

			{window.map((group) => {
				const isOpen = !collapsed.has(group.key);
				return (
					<section className="em-list__day" key={group.key}>
						<button
							type="button"
							className="em-list__day-head"
							aria-expanded={isOpen}
							onClick={() => toggleDay(group.key)}
						>
							<FontAwesomeIcon icon={faCalendarDay} aria-hidden="true" />
							<span className="em-list__day-name">{group.heading}</span>
							<CCBadge tone="neutral">
								{group.events.length}{" "}
								{group.events.length === 1 ? "event" : "events"}
							</CCBadge>
							<FontAwesomeIcon
								className="em-list__day-chevron"
								icon={isOpen ? faChevronUp : faChevronDown}
								aria-hidden="true"
							/>
						</button>

						{isOpen && (
							<ul className="em-list__rows">
								{group.events.map((evt) => {
									const id = evt._id ?? String(evt.id);
									const rgb = hexToRgbTriple(evt.color);
									const time = formatTimeRange(evt.start, evt.end);
									const picked = selection?.selected.has(id) ?? false;
									return (
										<li
											className={`em-list__row${picked ? " em-list__row--picked" : ""}`}
											key={id}
											style={{ borderLeftColor: `rgb(${rgb})` }}
											/* While a tag is armed the WHOLE ROW is the target,
											   the way the whole card is in the grid — not a tick
											   box in a column of its own. The row is where the
											   pointer already is, and a column that exists only
											   in one mode shifts every other cell sideways the
											   moment the mode turns on.

											   role/tabIndex/onKeyDown because an <li> is not a
											   control: without them this would be a click target
											   no keyboard can reach, and with the tick boxes gone
											   there is nothing else on the row that chooses. */
											{...(selection
												? {
														role: "button" as const,
														tabIndex: 0,
														"aria-pressed": picked,
														onClick: () => selection.onToggle(id),
														onKeyDown: (
															e: React.KeyboardEvent<HTMLLIElement>,
														) => {
															if (e.key !== "Enter" && e.key !== " ") return;
															e.preventDefault();
															selection.onToggle(id);
														},
													}
												: {})}
										>
											<span className="em-list__time">{time || "All day"}</span>
											{/* The thumbnail shares the TITLE's grid cell rather
											    than taking a column of its own. A column would have
											    to exist on every row to keep the four aligned, so
											    every event without a banner — which is most of them
											    — would pay 36px of empty space for the few that
											    have one. Inside the cell it simply is not there. */}
											<span className="em-list__titlecell">
												{evt.Banner?.ImageUrl && (
													<img
														className="em-list__thumb"
														src={evt.Banner.ImageUrl}
														alt=""
													/>
												)}
												{/* A plain span while a tag is armed: the row
												    itself is the control then, and a button nested
												    inside it would be a second tab stop doing the
												    same job — and would fire both handlers on one
												    click, toggling the event straight back off. */}
												{selection ? (
													<span className="em-list__title em-list__title--plain">
														{evt.title}
													</span>
												) : (
													<button
														type="button"
														className="em-list__title"
														onClick={() => onOpen(id)}
													>
														{evt.title}
													</button>
												)}
											</span>
											<span className="em-list__location">
												{evt.location && (
													<>
														<FontAwesomeIcon
															icon={faLocationDot}
															aria-hidden="true"
														/>{" "}
														{evt.location}
													</>
												)}
											</span>
											<span className="em-list__chips">
												{(chips.get(id) ?? []).map((chip) =>
													/* The app's own Tooltip rather than a `title`
													   attribute: a browser tooltip is unstyled, slow to
													   appear and cannot hold a list, and the audience
													   chip's whole job is to stand in for one. A chip
													   with nothing behind it is just a badge. */
													chip.detail ? (
														<Tooltip
															key={chip.id}
															label={chip.label}
															details={<ChipDetail lines={chip.detail} />}
															className="em-chip-trigger"
														>
															<CCBadge
																tone={chip.tone}
																className={chipClass(chip)}
															>
																{chip.label}
															</CCBadge>
														</Tooltip>
													) : (
														<CCBadge
															key={chip.id}
															tone={chip.tone}
															className={chipClass(chip)}
														>
															{chip.label}
														</CCBadge>
													),
												)}
											</span>
										</li>
									);
								})}
							</ul>
						)}
					</section>
				);
			})}

			<div className="em-browse-edge" ref={bottomRef}>
				<BottomEdge
					atEdge={atEnd}
					search={search}
					browse={{ loadingLater, onLoadLater, scrollable: canScroll }}
				/>
			</div>
		</div>
	);
};

/**
 * Memoised because the page above re-renders for things this list does not care
 * about — the export flag, the create dialog, the calendar's tooltip. At a few
 * thousand rows, re-rendering for each of those is most of what made the view
 * feel stuck. Depends on `onOpen` and the two loaders being stable, which
 * EventList and useBrowseEvents see to.
 */
export const EventListRows = React.memo(EventListRowsInner);
